/* eslint-disable @typescript-eslint/no-explicit-any */

import { api, ApiResponse } from '@/api/api';
import { JOB_CANDIDATE_URLS } from '@/api/url';
import { AlertsAndFollowUpQuestion } from '@/types/AnalysisResult';
import {
  CandidateWithJobContext,
  CandidateWithJobContextBasicInfo,
  CandidateNote,
  CandidateUserScore
} from '@/types/Candidate';
import { JobCandidateStatus } from '@/types/enums';
import { isAxiosError } from 'axios';

export type GetJobCandidatesSortBy = 'score' | 'status' | 'created_at' | 'name';
export type SortOrder = 'asc' | 'desc';

export interface GetJobCandidatesParams {
  page: number;
  limit: number;
  sort_by: GetJobCandidatesSortBy;
  sort_order: SortOrder;
  keyword: string;
  status?: JobCandidateStatus[];
}

export interface GetJobCandidatesResponse {
  candidates: CandidateWithJobContextBasicInfo[];
  current_page: number;
  total_pages: number;
  total_count: number;
}

export const apiGetJobCandidates = async ({
  jobId,
  params
}: {
  jobId: string;
  params: GetJobCandidatesParams;
}): Promise<ApiResponse<GetJobCandidatesResponse>> => {
  try {
    // Custom params serializer to handle arrays without [] notation
    const paramsSerializer = (params: any) => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, add each value with the same key (no [] notation)
          value.forEach((item) => {
            searchParams.append(key, String(item));
          });
        } else if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });

      return searchParams.toString();
    };

    const response = await api.get<ApiResponse<GetJobCandidatesResponse>>(
      JOB_CANDIDATE_URLS.GET_ALL(jobId),
      {
        params,
        paramsSerializer
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface CreateJobCandidatePayload {
  resume: File;
}

export const apiCreateJobCandidate = async ({
  jobId,
  payload
}: {
  jobId: string;
  payload: CreateJobCandidatePayload;
}): Promise<ApiResponse<CandidateWithJobContext>> => {
  try {
    const formData = new FormData();
    formData.append('resume', payload.resume);

    const response = await api.post<ApiResponse<CandidateWithJobContext>>(
      JOB_CANDIDATE_URLS.CREATE(jobId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface CreateJobCandidateFromVeteransResumePayload {
  resume: File;
}

export const apiCreateJobCandidateFromVeteransResume = async ({
  jobId,
  payload
}: {
  jobId: string;
  payload: CreateJobCandidateFromVeteransResumePayload;
}): Promise<ApiResponse<CandidateWithJobContext>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.resume);

    const response = await api.post<ApiResponse<CandidateWithJobContext>>(
      JOB_CANDIDATE_URLS.CREATE_FROM_VETERANS_RESUME(jobId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiGetJobCandidateDetails = async ({
  jobId,
  candidateId
}: {
  jobId: string;
  candidateId: string;
}): Promise<ApiResponse<CandidateWithJobContext>> => {
  try {
    const response = await api.get<ApiResponse<CandidateWithJobContext>>(
      JOB_CANDIDATE_URLS.GET_DETAILS(jobId, candidateId)
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateJobCandidateStatusPayload {
  status: JobCandidateStatus;
}

export interface UpdateJobCandidateStatusResponse {
  status: JobCandidateStatus;
  progress: JobCandidateStatus[];
}

export const apiUpdateJobCandidateStatus = async ({
  jobId,
  candidateId,
  payload
}: {
  jobId: string;
  candidateId: string;
  payload: UpdateJobCandidateStatusPayload;
}): Promise<ApiResponse<UpdateJobCandidateStatusResponse>> => {
  try {
    const response = await api.put<ApiResponse<UpdateJobCandidateStatusResponse>>(
      JOB_CANDIDATE_URLS.UPDATE_STATUS(jobId, candidateId),
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateJobCandidateScorePayload {
  type: 'resume' | 'interview';
  score: number;
}

export const apiUpdateJobCandidateScore = async ({
  jobId,
  candidateId,
  payload
}: {
  jobId: string;
  candidateId: string;
  payload: UpdateJobCandidateScorePayload;
}): Promise<ApiResponse<CandidateUserScore>> => {
  try {
    const response = await api.put<ApiResponse<CandidateUserScore>>(
      JOB_CANDIDATE_URLS.UPDATE_SCORE(jobId, candidateId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateJobCandidateNotesPayload {
  notes: CandidateNote[];
}

export const apiUpdateJobCandidateNotes = async ({
  jobId,
  candidateId,
  payload
}: {
  jobId: string;
  candidateId: string;
  payload: UpdateJobCandidateNotesPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(
      JOB_CANDIDATE_URLS.UPDATE_NOTES(jobId, candidateId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateJobCandidateFollowupQuestionsPayload {
  followup_questions: AlertsAndFollowUpQuestion[];
}

export const apiUpdateJobCandidateFollowupQuestions = async ({
  jobId,
  candidateId,
  payload
}: {
  jobId: string;
  candidateId: string;
  payload: UpdateJobCandidateFollowupQuestionsPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(
      JOB_CANDIDATE_URLS.UPDATE_FOLLOWUP_QUESTIONS(jobId, candidateId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetPreviousAndNextJobCandidatesResponse {
  previous_candidate: CandidateWithJobContextBasicInfo;
  next_candidate: CandidateWithJobContextBasicInfo;
}

export const apiGetPreviousAndNextJobCandidates = async ({
  jobId,
  candidateId
}: {
  jobId: string;
  candidateId: string;
}): Promise<ApiResponse<GetPreviousAndNextJobCandidatesResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetPreviousAndNextJobCandidatesResponse>>(
      JOB_CANDIDATE_URLS.GET_PREVIOUS_AND_NEXT_CANDIDATES(jobId, candidateId)
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiDeleteJobCandidate = async ({
  jobId,
  candidateId
}: {
  jobId: string;
  candidateId: string;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete<ApiResponse<void>>(
      JOB_CANDIDATE_URLS.DELETE(jobId, candidateId)
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};
